import axios from 'axios';

// App version for tracking deployments
const APP_VERSION = 'v1.0.1';

// Token refresh configuration
const TOKEN_REFRESH_INTERVAL = 24 * 60 * 60 * 1000; // Refresh every 24 hours
let isRefreshing = false;

// Get last refresh time from storage or use current time
const getLastTokenRefresh = (): number => {
  const stored = localStorage.getItem('tokenRefreshedAt');
  return stored ? parseInt(stored, 10) : Date.now();
};

const setLastTokenRefresh = (): void => {
  localStorage.setItem('tokenRefreshedAt', Date.now().toString());
};

// Create a unique request ID for logging
const createRequestId = () => {
  return `req-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
};

// Function to refresh the token
const refreshToken = async () => {
  if (isRefreshing) return;
  
  const token = localStorage.getItem('token');
  if (!token) return;
  
  // Only refresh if enough time has passed
  const timeSinceLastRefresh = Date.now() - getLastTokenRefresh();
  if (timeSinceLastRefresh < TOKEN_REFRESH_INTERVAL) return;
  
  isRefreshing = true;
  
  try {
    console.log('[TokenRefresh] Attempting to refresh token...');
    const response = await axios.post(
      `${process.env.REACT_APP_API_URL || 'http://localhost:9000/api/v1'}/auth/refresh-token`,
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    if (response.data?.token) {
      localStorage.setItem('token', response.data.token);
      setLastTokenRefresh();
      console.log('[TokenRefresh] Token refreshed successfully');
    }
  } catch (error) {
    console.error('[TokenRefresh] Failed to refresh token:', error);
    // Don't clear token on refresh failure - let the normal 401 handling deal with it
  } finally {
    isRefreshing = false;
  }
};

// Set up periodic token refresh when user is active
if (typeof window !== 'undefined') {
  // Refresh token on user activity (but throttled)
  let activityTimeout: NodeJS.Timeout | null = null;
  
  const handleActivity = () => {
    if (activityTimeout) clearTimeout(activityTimeout);
    activityTimeout = setTimeout(() => {
      refreshToken();
    }, 5000); // Wait 5 seconds after activity to refresh
  };
  
  // Listen for user activity
  ['click', 'keypress', 'scroll', 'touchstart'].forEach(event => {
    window.addEventListener(event, handleActivity, { passive: true });
  });
  
  // Also refresh on page visibility change (when user returns to the app)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      refreshToken();
    }
  });
}

// Helper function to safely access environment variables
const getEnvValue = (key: string): string => {
  if (process.env[key]) {
    return process.env[key] as string;
  }
  return '';
};

// Get API URL from environment or use default
const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:9000/api/v1';

// Log API configuration for debugging
console.log('=== API Configuration ===');
console.log('App Version:', APP_VERSION);
console.log('API URL configured as:', apiUrl);
console.log('Environment:', process.env.NODE_ENV);
console.log('Running in production:', getEnvValue('PROD') === 'true' ? 'Yes' : 'No');
console.log('========================');

const instance = axios.create({
  baseURL: apiUrl,
  headers: {
    'Content-Type': 'application/json',
    'X-App-Version': APP_VERSION
  },
});

// Request interceptor with logging
instance.interceptors.request.use(
  (config) => {
    const requestId = createRequestId();
    config.headers['X-Request-ID'] = requestId;
    
    // Get token from storage
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      console.log(`[${requestId}] Adding Authorization header with token`);
    } else {
      console.log(`[${requestId}] No token found in storage`);
    }
    
    // Handle FormData correctly - don't set Content-Type for FormData
    // as the browser needs to set it with the proper boundary
    if (config.data instanceof FormData) {
      console.log(`[${requestId}] FormData detected, setting Content-Type to undefined`);
      config.headers['Content-Type'] = undefined;
    }
    
    // Log the request details
    console.log(`[${requestId}] API Request: ${config.method?.toUpperCase()} ${config.baseURL}${config.url}`);
    console.log(`[${requestId}] Request headers:`, config.headers);
    
    if (config.data) {
      // Log request body but omit sensitive fields
      if (config.data instanceof FormData) {
        console.log(`[${requestId}] Request payload: [FormData object]`);
      } else {
        const loggableData = { ...config.data };
        if (loggableData.password) loggableData.password = '[REDACTED]';
        console.log(`[${requestId}] Request payload:`, loggableData);
      }
    }
    
    // Save request ID for response logging
    (config as any).requestId = requestId;
    
    return config;
  },
  (error) => {
    console.error('Request error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor with logging
instance.interceptors.response.use(
  (response) => {
    const requestId = (response.config as any).requestId || 'unknown';
    
    console.log(`[${requestId}] API Response: ${response.status} ${response.statusText}`);
    console.log(`[${requestId}] Response headers:`, response.headers);
    
    // Enhanced logging for debugging UI rendering issues
    const responseData = response.data;
    const url = response.config.url || '';
    
    // More detailed logging for specific endpoints
    if (url.includes('design-cutting') || 
        url.includes('refinish-assignments') || 
        url.includes('dispatch') || 
        url.includes('users')) {
      console.log(`[${requestId}] DEBUG - Full response for ${url}:`, responseData);
      
      if (Array.isArray(responseData)) {
        console.log(`[${requestId}] Array response length: ${responseData.length}`);
        
        if (responseData.length > 0) {
          console.log(`[${requestId}] First item sample:`, responseData[0]);
          console.log(`[${requestId}] Data types:`, 
            Object.entries(responseData[0]).map(([key, value]) => 
              `${key}: ${typeof value}${value === null ? ' (null)' : ''}`
            )
          );
        } else {
          console.log(`[${requestId}] Empty array returned`);
        }
      } else if (responseData && typeof responseData === 'object') {
        console.log(`[${requestId}] Object response keys:`, Object.keys(responseData));
      }
    } else {
      // Regular logging for other endpoints
      if (typeof responseData === 'object') {
        console.log(`[${requestId}] Response data structure:`, Object.keys(responseData));
        
        // For small responses, log full data
        if (JSON.stringify(responseData).length < 1000) {
          console.log(`[${requestId}] Response data:`, responseData);
        } else {
          console.log(`[${requestId}] Response data too large to log in full`);
        }
      } else {
        console.log(`[${requestId}] Response data type:`, typeof responseData);
      }
    }
    
    return response;
  },
  (error) => {
    const requestId = (error.config as any)?.requestId || 'unknown';
    
    // Enhanced error logging with more details
    console.error(`[${requestId}] API Error:`, {
      url: error.config?.url,
      method: error.config?.method,
      message: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      headers: error.response?.headers,
    });
    
    // Don't automatically redirect on 401 errors for the login endpoint
    // This could cause redirect loops
    if (error.response?.status === 401 && !error.config?.url?.includes('/auth/login')) {
      console.log(`[${requestId}] Unauthorized (401) response from non-login endpoint, logging error but not redirecting`);
      console.log('Auth state in localStorage:', {
        token: !!localStorage.getItem('token'),
        user: !!localStorage.getItem('user')
      });
      
      // Only clear auth data if we receive a 401 from a non-login endpoint
      // and the user is not on the login page already
      if (!window.location.pathname.includes('/login')) {
        console.log(`[${requestId}] User not on login page, clearing auth data`);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        
        // Add delay to prevent potential redirect loops
        setTimeout(() => {
          console.log(`[${requestId}] Redirecting to login page after delay`);
          window.location.href = '/login?session=expired';
        }, 100);
      }
    }
    
    return Promise.reject(error);
  }
);

export default instance; 